import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// import { DeviceSelector } from './device/DeviceSelector';
// import { MediaControls } from './media/MediaControls';
import { IOSCameraMedia } from './media/IOSCameraMedia';
import { Chat } from './chat/Chat';
import { useHLSStreamer, MediaType } from '../../hooks/useHLSStreamer';
import { useSocket } from '../../contexts/SocketContext';
import { apiService } from '../../api/api.service';
import { getAdminDisplayName } from '../../utils/adminNameUtils';
import type { StreamPersistedState } from '../../hooks/useStreamPersistence';
import type { ISteramMessage } from '../../types';
import { logDeviceInfo } from '../../utils/deviceDetectionUtils';
import { EyeIcon, EyeOffIcon, ChatDotsIcon, PersonIcon } from '../../components/icons';
import CloseIcon from '../../components/icons/CloseIcon';
import { ChatMessage } from './chat/ChatMessage';
import styles from './Livestream.module.scss';

interface AdminStreamProps {
	userName: string | undefined;
}

const STREAM_ROOM_ID = 'main-stream';

// --- Убран ключ для хранения таймера в localStorage ---

export const AdminStream: React.FC<AdminStreamProps> = ({ userName = 'Администратор' }) => {
	const { t } = useTranslation();
	const { socket } = useSocket();

	// ref для локального видео
	const localVideoRef = useRef<HTMLVideoElement>(null);
	// ref для повернутого видео (отправляемого в сеть)
	const rotatedVideoRef = useRef<HTMLVideoElement>(null);

	// ref для отслеживания готовности транспортов (чтобы избежать замыкания в циклах)
	const transportsReadyRef = useRef(false);

	// Состояние для лоадера при остановке стрима
	const [isStopping, setIsStopping] = useState(false);

	// Задержка перед возможностью запуска нового стрима (для полного освобождения ресурсов)
	const [cooldownEndTime, setCooldownEndTime] = useState<number | null>(null);
	const [cooldownSeconds, setCooldownSeconds] = useState(0);

	// Состояния для управления интерфейсом
	const [showSettingsModal, setShowSettingsModal] = useState(false);
	const [showFullChat, setShowFullChat] = useState(false);
	const [showChatOverlay, setShowChatOverlay] = useState(true);
	const [visibleMessages, setVisibleMessages] = useState<ISteramMessage[]>([]);
	const [showPermissionsInfo, setShowPermissionsInfo] = useState(false);

	// --- Хук для работы с HLS стримом ---
	const {
		state,
		joinRoom,
		startMedia,
		stopMedia,
		// muteAudio,
		// unmuteAudio,
		// changeAudioDevice,
		muteVideo,
		unmuteVideo,
		// changeVideoDevice,
		startStream: startHLSStream,
		stopStream: stopHLSStream,
		disconnectAll,
	} = useHLSStreamer({
		roomId: STREAM_ROOM_ID,
		userName: getAdminDisplayName(userName),
		localVideoRef,
		rotatedVideoRef
	});

	// Обновляем ref при изменении состояния транспортов
	useEffect(() => {
		transportsReadyRef.current = state.transportsReady || false;
	}, [state.transportsReady]);

	// --- Состояния стрима ---
	const [streamState, setStreamState] = useState<StreamPersistedState>({
		streamStarted: false,
		chatMessages: [],
		audioActive: false,
		videoActive: false,
		audioDeviceId: '',
		videoDeviceId: '',
	});

	// --- Название стрима ---
	const [streamTitle, setStreamTitle] = useState('');

	// Количество зрителей (из API комнаты)
	const [viewers, setViewers] = useState<number | null>(null);

	// Таймер задержки между стримами (cooldown)
	useEffect(() => {
		if (!cooldownEndTime) {
			setCooldownSeconds(0);
			return;
		}

		const updateCooldown = () => {
			const secondsLeft = Math.max(0, Math.floor((cooldownEndTime - Date.now()) / 1000));
			setCooldownSeconds(secondsLeft);

			if (secondsLeft <= 0) {
				setCooldownEndTime(null);
				localStorage.removeItem('streamCooldownEndTime');
				console.log('✅ Задержка между стримами завершена');
			}
		};

		updateCooldown();
		const interval = setInterval(updateCooldown, 1000);
		return () => clearInterval(interval);
	}, [cooldownEndTime]);

	// Автоматический вход в комнату при монтировании
	useEffect(() => {
		if (!state.joined) {
			joinRoom();
		}

		// Восстанавливаем задержку из localStorage (если страница была перезагружена)
		const savedCooldownTime = localStorage.getItem('streamCooldownEndTime');
		if (savedCooldownTime) {
			const cooldownTime = parseInt(savedCooldownTime, 10);
			if (cooldownTime > Date.now()) {
				setCooldownEndTime(cooldownTime);
				console.log('⏳ Восстановлена задержка до следующего стрима');
			} else {
				// Задержка уже истекла - удаляем из localStorage
				localStorage.removeItem('streamCooldownEndTime');
			}
		}

		// Запрашиваем историю чата
		const getChat = async () => {
			const messages = await apiService.streamChat()

			if (messages) {
				setStreamState(prev => ({ ...prev, chatMessages: messages }))
			}
		}

		getChat()

		// Логируем информацию об устройстве для отладки
		logDeviceInfo();
	}, [state.joined, joinRoom]);


	// Слушаем входящие сообщения и ошибки соединения
	useEffect(() => {
		if (!socket) return;

		const handleChatMessage = (data: { userName: string; message: string; timestamp: string }) => {
			const chatMessage: ISteramMessage = {
				id: Date.now() + Math.random(),
				username: data.userName,
				message: data.message,
				created_at: data.timestamp,
			};
			setStreamState(prev => ({ ...prev, chatMessages: [...prev.chatMessages, chatMessage] }))
		};

		const handleDisconnect = (reason: string) => {
			console.log('🔴 Socket disconnected:', reason);
			// При отключении сокета автоматически выходим из комнаты
			if (state.joined) {
				console.log('🚪 Auto-leaving room due to socket disconnect');
				// Используем disconnectAll для корректного выхода из комнаты
				disconnectAll();
			}
		};

		const handleConnectError = (error: any) => {
			console.error('❌ Socket connection error:', error);
			// При ошибке соединения также сбрасываем состояние
			if (state.joined) {
				console.log('🚪 Auto-leaving room due to connection error');
				disconnectAll();
			}
		};

		socket.on('chatMessage', handleChatMessage);
		socket.on('disconnect', handleDisconnect);
		socket.on('connect_error', handleConnectError);

		return () => {
			socket.off('chatMessage', handleChatMessage);
			socket.off('disconnect', handleDisconnect);
			socket.off('connect_error', handleConnectError);
		};
	}, [socket, state.joined, disconnectAll]);

	// Отправка сообщения
	const handleSendMessage = (message: string) => {
		if (!socket) return;

		const messageData = {
			roomId: STREAM_ROOM_ID,
			userName: getAdminDisplayName(userName),
			message,
			timestamp: new Date().toISOString(),
		};

		socket.emit('sendChatMessage', messageData);
	};

	// Индикаторы загрузки
	const isLoading = !state.deviceReady || !state.transportsReady;

	// КРИТИЧЕСКИ ВАЖНАЯ ФУНКЦИЯ: Проверка и запрос разрешений на камеру и микрофон
	// ВАЖНО: Браузеры НЕ позволяют программно сбрасывать разрешения (это безопасность).
	// Но вызов getUserMedia ВСЕГДА создает новый MediaStream и проверяет доступность устройств,
	// даже если разрешения уже были даны ранее. Это гарантирует что устройства доступны
	// и не используются другими приложениями при каждом запуске стрима.
	const requestMediaPermissions = async (): Promise<{ success: boolean; error?: any }> => {
		console.log('🎥 ЗАПРОС РАЗРЕШЕНИЙ: Камера и микрофон...');
		let permissionStream: MediaStream | null = null;

		try {
			permissionStream = await navigator.mediaDevices.getUserMedia({
				video: {
					facingMode: 'user',
					width: { ideal: 1280 },
					height: { ideal: 720 }
				},
				audio: {
					echoCancellation: true,
					noiseSuppression: true,
					autoGainControl: true
				}
			});

			console.log('✅ РАЗРЕШЕНИЯ ПОЛУЧЕНЫ: Камера и микрофон доступны');

			// Проверяем что оба типа треков получены
			const videoTracks = permissionStream.getVideoTracks();
			const audioTracks = permissionStream.getAudioTracks();

			if (videoTracks.length === 0) {
				throw new Error('Не удалось получить доступ к камере');
			}

			if (audioTracks.length === 0) {
				throw new Error('Не удалось получить доступ к микрофону');
			}

			console.log('✅ Проверка треков пройдена:', {
				video: videoTracks.length,
				audio: audioTracks.length
			});

			return { success: true };

		} catch (error: any) {
			console.error('❌ ОШИБКА ПОЛУЧЕНИЯ РАЗРЕШЕНИЙ:', error);
			return { success: false, error };

		} finally {
			// Останавливаем временный поток после получения разрешений
			if (permissionStream) {
				permissionStream.getTracks().forEach(track => {
					track.stop();
					console.log(`🛑 Остановлен временный трек: ${track.kind}`);
				});
			}
		}
	};

	// Управление медиа с учётом выбранных устройств
	const handleStartMedia = async (type: MediaType) => {
		// Проверяем готовность транспортов с повторными попытками
		let attempts = 0;
		const maxAttempts = 50; // 50 попыток * 200ms = 10 секунд максимум

		while (!transportsReadyRef.current && attempts < maxAttempts) {
			if (attempts === 0) {
				console.warn('⚠️ Transports not ready, waiting...');
			}
			attempts++;
			await new Promise(resolve => setTimeout(resolve, 200));
		}

		if (!transportsReadyRef.current) {
			console.error('⏱️ Timeout: Transports not ready after 10 seconds');
			throw new Error('Producer transport not ready');
		}

		if (attempts > 0) {
			console.log(`✅ Transports are ready (waited ${attempts * 200}ms)`);
		}

		if (type === 'audio') {
			await startMedia('audio', streamState.audioDeviceId);
		} else if (type === 'video') {
			await startMedia('video', streamState.videoDeviceId);
		} else {
			await startMedia(type);
		}
	};


	// Начать стрим
	const startStream = async () => {
		if (streamState.streamStarted) return;

		// КРИТИЧЕСКИ ВАЖНО: Проверяем задержку между стримами
		if (cooldownEndTime && cooldownSeconds > 0) {
			alert(`⏳ Подождите ${cooldownSeconds} секунд перед запуском нового стрима.\n\nЭто необходимо для полного освобождения ресурсов камеры и микрофона.`);
			return;
		}

		if (!streamTitle.trim()) {
			alert('Введите название трансляции');
			return;
		}

		// Проверяем готовность транспортов перед началом стрима
		if (!transportsReadyRef.current) {
			console.error('❌ Cannot start stream: transports not ready');
			alert('Подождите, идет подключение к серверу...');
			return;
		}

		// КРИТИЧЕСКИ ВАЖНО: Запрашиваем разрешения на камеру и микрофон ПРИ КАЖДОМ старте стрима
		const permissionsResult = await requestMediaPermissions();
		if (!permissionsResult.success) {
			console.error('❌ Разрешения не получены - стрим НЕ запускается');

			const error = permissionsResult.error;

			if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
				setShowPermissionsInfo(true);
			} else if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
				alert('❌ Камера или микрофон не найдены.\n\nПроверьте подключение устройств.');
			} else if (error?.name === 'NotReadableError' || error?.name === 'TrackStartError') {
				alert('❌ Камера или микрофон уже используются другим приложением.\n\nЗакройте другие приложения и попробуйте снова.');
			} else {
				alert('❌ Не удалось получить доступ к камере и микрофону.\n\nПроверьте настройки браузера.');
			}

			return;
		}

		console.log('✅ РАЗРЕШЕНИЯ ПОДТВЕРЖДЕНЫ - Продолжаем запуск стрима');
		console.log('🚀 Starting stream');

		setStreamState(prev => ({ ...prev, streamStarted: true }));
		setStreamState(prev => ({ ...prev, chatMessages: [] }));

		// Текст уведомления
		const notificationText = 'Прямая трансляция началась';

		// Отправляем уведомление ПЕРЕД запуском стрима (fire-and-forget, не блокирует запуск)
		(async () => {
			try {
				const token = document.cookie.split(';').find(c => c.trim().startsWith('access_token='));
				if (!token) {
					console.warn('⚠️ Отсутствует access_token, пропускаем отправку уведомления');
					return;
				}

				console.log('📤 Отправка уведомления о стриме...');
				await apiService.sendNotification({
					title: 'Трансляция',
					body: notificationText,
					type: 'immediate',
					language: 'RU',
					is_active: true,
					consider_timezone: false,
					active_subscriptions: true,
					inactive_subscriptions: false,
					data: {
						screen: 'Live',
						tab: 'Live',
						language: 'RU'
					}
				});
				console.log('✅ Уведомление о стриме успешно отправлено');
			} catch (notificationError: any) {
				console.error('❌ Ошибка отправки уведомления:', notificationError);
				console.error('📊 Детали ошибки:', {
					message: notificationError?.message,
					status: notificationError?.response?.status,
					data: notificationError?.response?.data,
					url: notificationError?.config?.url
				});
			}
		})();

		// Запускаем HLS стрим (не ждем завершения отправки уведомления)
		try {
			await apiService.streamStart();
			await startHLSStream(streamTitle.trim(), false);
			console.log('✅ HLS stream started successfully');

		} catch (error) {
			console.error('Failed to start HLS stream:', error);
			return;
		}

		// Сразу запускаем медиа (задержки нет)
		try {
			await handleStartMedia('video');
			setStreamState(prev => ({ ...prev, videoActive: true }));

			// ВАЖНО: Переключаем видео для инициализации canvas
			await new Promise(resolve => setTimeout(resolve, 1500)); // Ждем 1.5 сек
			muteVideo(); // Выключаем
			await new Promise(resolve => setTimeout(resolve, 500)); // Ждем 0.5 сек
			await unmuteVideo(streamState.videoDeviceId || undefined); // Включаем

			await handleStartMedia('audio');
			setStreamState(prev => ({ ...prev, audioActive: true }));
		} catch (error) {
			console.error('Failed to start media:', error);
			alert('Не удалось запустить камеру и микрофон. Проверьте разрешения и попробуйте снова.');
		}
	};

	// Остановить стрим
	const stopStream = React.useCallback(async () => {
		setIsStopping(true);

		setStreamState(prev => ({ ...prev, streamStarted: false }));
		setStreamTitle('');

		try {
			console.log('🛑 Начинаем остановку стрима и освобождение всех ресурсов...');

			// 1. Останавливаем все медиа-треки вручную
			if (localVideoRef.current?.srcObject) {
				const stream = localVideoRef.current.srcObject as MediaStream;
				stream.getTracks().forEach(track => {
					track.stop();
					console.log(`🛑 Остановлен трек: ${track.kind} (${track.label})`);
				});
				localVideoRef.current.srcObject = null;
			}

			if (rotatedVideoRef.current?.srcObject) {
				const stream = rotatedVideoRef.current.srcObject as MediaStream;
				stream.getTracks().forEach(track => {
					track.stop();
					console.log(`🛑 Остановлен повернутый трек: ${track.kind}`);
				});
				rotatedVideoRef.current.srcObject = null;
			}

			// 2. Останавливаем HLS стрим на сервере
			await stopHLSStream();
			await apiService.streamStop({ messages: streamState.chatMessages });

			// 3. ПОЛНАЯ очистка всех ресурсов WebRTC/MediaSoup
			await disconnectAll();

			console.log('✅ Stream stopped and resources cleaned');

			// 4. Очищаем все возможные медиа-потоки в браузере
			// Получаем все активные медиа-устройства и останавливаем их
			try {
				const devices = await navigator.mediaDevices.enumerateDevices();
				console.log(`📱 Найдено устройств: ${devices.length}`);
			} catch (e) {
				console.log('⚠️ Не удалось перечислить устройства:', e);
			}

			// 5. Задержка для полного освобождения устройств браузером
			await new Promise(resolve => setTimeout(resolve, 2000));

			console.log('✅ Все медиа-ресурсы освобождены');

			// 6. Устанавливаем задержку 60 секунд перед возможностью запуска нового стрима
			const cooldownTime = Date.now() + 60 * 1000; // 60 секунд
			localStorage.setItem('streamCooldownEndTime', cooldownTime.toString());
			console.log('⏳ Установлена задержка 60 секунд перед следующим стримом');

			// 7. Переподключаемся к комнате для следующего стрима
			joinRoom();

			// 8. Перезагружаем страницу для полной очистки состояния
			console.log('🔄 Reloading page to clear state...');
			setTimeout(() => {
				window.location.reload();
			}, 1000); // Небольшая задержка для завершения всех операций

		} catch (error) {
			console.error('Failed to stop stream:', error);
		} finally {
			setIsStopping(false);
		}
	}, [stopHLSStream, streamState.chatMessages, disconnectAll, joinRoom]);

	// --- Корректный выход из комнаты при перезагрузке/закрытии страницы ---
	useEffect(() => {
		const handleBeforeUnload = () => {
			// При перезагрузке или закрытии страницы останавливаем стрим и корректно выходим из комнаты
			try {
				if (streamState.streamStarted) {
					console.log('🛑 Stopping stream due to page unload');
					// Синхронно останавливаем стрим
					stopHLSStream();
					apiService.streamStop({ messages: streamState.chatMessages });
				}
				if (socket && state.joined) {
					console.log('🚪 Leaving room due to page unload');
					// Отправляем событие leave на сервер для освобождения комнаты
					socket.emit('leave', { room_id: STREAM_ROOM_ID }, (response: any) => {
						console.log('🚪 Leave response on unload:', response);
					});
				}
			} catch (error) {
				console.error('Error stopping stream and leaving room on beforeunload:', error);
			}
		};

		// Добавляем beforeunload для корректного выхода из комнаты
		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [socket, state.joined, streamState.streamStarted, streamState.chatMessages, stopHLSStream]); // Добавляем зависимости

	// Автоматический запуск камеры и микрофона после восстановления состояния и готовности транспорта
	// Этот эффект нужен для восстановления медиа после реконнекта
	useEffect(() => {
		if (
			streamState.streamStarted &&
			state.joined &&
			state.transportsReady
		) {
			// Запускаем видео только если оно должно быть активно, но еще не активно в state
			if (streamState.videoActive && !state.videoActive) {
				console.log('🔄 Restoring video after reconnect...');
				handleStartMedia('video');
			}
			// Запускаем аудио только если оно должно быть активно, но еще не активно в state
			if (streamState.audioActive && !state.audioActive) {
				console.log('🔄 Restoring audio after reconnect...');
				handleStartMedia('audio');
			}
		}
		// eslint-disable-next-line
	}, [streamState.streamStarted, streamState.videoActive, streamState.audioActive, state.joined, state.transportsReady, state.videoActive, state.audioActive]);

	// --- Пинг для поддержания соединения с сервером ---
	useEffect(() => {
		if (!socket) return;
		const interval = setInterval(() => {
			socket.emit('ping');
		}, 30000);
		return () => clearInterval(interval);
	}, [socket]);

	// --- Опрос количества зрителей при активном стриме ---
	useEffect(() => {
		if (!streamState.streamStarted) {
			setViewers(null);
			return;
		}
		const fetchViewers = async () => {
			try {
				const data = await apiService.getRoomViewers(STREAM_ROOM_ID);
				setViewers(data.viewers);
			} catch {
				setViewers(null);
			}
		};
		fetchViewers();
		const interval = setInterval(fetchViewers, 3000);
		return () => clearInterval(interval);
	}, [streamState.streamStarted]);

	// --- Управление видимыми сообщениями чата ---
	useEffect(() => {
		if (showFullChat) {
			// В полноэкранном чате показываем все сообщения
			setVisibleMessages(streamState.chatMessages);
		} else {
			// Показываем только последние 3 сообщения
			const lastThreeMessages = streamState.chatMessages.slice(-3);
			setVisibleMessages(lastThreeMessages);
		}
	}, [streamState.chatMessages, showFullChat]);

	// --- Таймер для плавного исчезновения сообщений (только для оверлея) ---
	useEffect(() => {
		if (showFullChat || visibleMessages.length === 0) return;

		const timers: NodeJS.Timeout[] = [];

		// Для каждого сообщения устанавливаем таймер на 5 секунд с плавным исчезновением
		visibleMessages.forEach((message, index) => {
			// Сначала делаем сообщение полупрозрачным через 4 секунды
			const fadeTimer = setTimeout(() => {
				// Добавляем класс fadeOut для плавного исчезновения
				const messageElement = document.querySelector(`[data-message-id="${message.id}"]`);
				if (messageElement) {
					messageElement.classList.add('fadeOut');
				}
			}, (index + 1) * 4000); // 4 секунды до начала исчезновения

			// Затем удаляем сообщение из состояния через 5 секунд
			const removeTimer = setTimeout(() => {
				setVisibleMessages(prev => prev.filter(msg => msg.id !== message.id));
			}, (index + 1) * 5000); // 5 секунд до полного удаления

			timers.push(fadeTimer, removeTimer);
		});

		return () => {
			timers.forEach(timer => clearTimeout(timer));
		};
	}, [visibleMessages, showFullChat]);

	// Cleanup при размонтировании компонента
	useEffect(() => {
		return () => {
			console.log('🔍 Component unmounting, cleaning up connections');
			// При размонтировании компонента разрываем все соединения
			try {
				// Останавливаем стрим если он активен
				if (streamState.streamStarted) {
					console.log('🛑 Stopping stream due to component unmount');
					// Синхронно останавливаем медиа
					stopMedia('audio');
					stopMedia('video');
				}

				// Используем disconnectAll для корректного выхода из комнаты
				if (state.joined) {
					console.log('🚪 Leaving room due to component unmount');
					// Вызываем disconnectAll для корректного выхода из комнаты
					// Это отправит событие leave на сервер
					disconnectAll();
				}
			} catch (error) {
				console.error('Error during component unmount cleanup:', error);
			}
		};
	}, [state.joined, disconnectAll, stopMedia, streamState.streamStarted]);

	return (
		<div className={styles.fullscreenContainer}>
			{/* Лоадер при остановке стрима */}
			{isStopping && (
				<div style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: 'rgba(0, 0, 0, 0.8)',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					zIndex: 9999,
					color: '#fff',
					fontSize: '24px',
					fontWeight: 'bold'
				}}>
					<div style={{
						width: '50px',
						height: '50px',
						border: '5px solid #f3f3f3',
						borderTop: '5px solid #4caf50',
						borderRadius: '50%',
						animation: 'spin 1s linear infinite',
						marginBottom: '20px'
					}} />
					<div>Остановка трансляции...</div>
					<style dangerouslySetInnerHTML={{
						__html: `
							@keyframes spin {
								0% { transform: rotate(0deg); }
								100% { transform: rotate(360deg); }
							}
						`
					}} />
				</div>
			)}

			{/* Кнопка настроек в правом верхнем углу */}
			<button
				className={styles.settingsButton}
				onClick={() => setShowSettingsModal(true)}
				title="Настройки стрима"
			>
				⚙️
			</button>

			{/* Кнопка переключения оверлея чата (3 сообщения) */}
			<button
				className={styles.chatToggleButton}
				onClick={() => setShowChatOverlay(!showChatOverlay)}
				title={showChatOverlay ? "Скрыть сообщения" : "Показать сообщения"}
			>
				{showChatOverlay ? <EyeOffIcon /> : <EyeIcon />}
			</button>

			{/* Кнопка открытия полноэкранного чата */}
			<button
				className={styles.chatFullscreenButton}
				onClick={() => setShowFullChat(true)}
				title="Открыть полноэкранный чат"
			>
				<ChatDotsIcon size={24} />
			</button>

			{/* Полноэкранное видео */}
			<div className={styles.videoContainer}>
				<IOSCameraMedia
					videoActive={!!state.videoActive}
					onVideoError={(error) => {
						console.error('iOS Camera error:', error);
					}}
					onVideoReady={(stream) => {
						if (localVideoRef.current) {
							localVideoRef.current.srcObject = stream;
						}
					}}
				/>
				{state.videoActive && (
					<video
						ref={rotatedVideoRef}
						autoPlay
						playsInline
						muted
						className={styles.fullscreenVideo}
					/>
				)}
				{!state.videoActive && (
					<div className={styles.waitingMessage}>
						Панель управления прямой трансляцией
					</div>
				)}

				{/* Информация о стриме */}
				{streamState.streamStarted && streamTitle && (
					<div className={styles.streamInfo}>
						<div className={styles.streamTitle}>
							{streamTitle}
						</div>
						<div className={styles.badgesRow}>
							<div className={styles.liveBadge}>
								<div className={styles.liveDot}></div>
								Прямой эфир
							</div>
							<div className={styles.viewersBadge}>
								<PersonIcon className={styles.viewersIcon} />
								<span className={styles.viewersCount}>{viewers ?? 0}</span>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Оверлей чата (3 последних сообщения) */}
			{!showFullChat && showChatOverlay && visibleMessages.length > 0 && (
				<div className={styles.chatOverlay}>
					{visibleMessages.map(msg => {
						const cleanMsgUsername = msg.username.replace(/^👑\s*/, '');
						const cleanUserName = getAdminDisplayName(userName);
						const isOwnMessage = cleanMsgUsername === cleanUserName;

						return (
							<div key={msg.id} data-message-id={msg.id}>
								<ChatMessage
									message={msg}
									isOwnMessage={isOwnMessage}
									isOverlay={true}
								/>
							</div>
						);
					})}
				</div>
			)}

			{/* Полноэкранный чат */}
			{showFullChat && (
				<div className={styles.fullChatContainer}>
					{/* Кнопка закрытия полноэкранного чата */}
					<button
						className={styles.fullChatCloseButton}
						onClick={() => setShowFullChat(false)}
						title="Закрыть чат"
					>
						<CloseIcon />
					</button>
					<Chat
						userName={getAdminDisplayName(userName)}
						roomId={STREAM_ROOM_ID}
						messages={streamState.chatMessages}
						onNewMessage={handleSendMessage}
						isFullscreen={true}
					/>
				</div>
			)}

			{/* Модальное окно настроек */}
			{showSettingsModal && (
				<div className={styles.modalOverlay} onClick={() => setShowSettingsModal(false)}>
					<div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
						<div className={styles.modalHeader}>
							<h3>Настройки трансляции</h3>
							<button
								className={styles.closeButton}
								onClick={() => setShowSettingsModal(false)}
							>
								✕
							</button>
						</div>

						{!streamState.streamStarted ? (
							<div className={styles.settingsForm}>
								<input
									type="text"
									value={streamTitle}
									onChange={e => setStreamTitle(e.target.value)}
									placeholder="Название трансляции"
									className={styles.streamTitleInput}
									disabled={cooldownSeconds > 0}
								/>

								{cooldownSeconds > 0 ? (
									<div style={{
										padding: '16px',
										backgroundColor: 'rgba(255, 152, 0, 0.1)',
										border: '1px solid rgba(255, 152, 0, 0.3)',
										borderRadius: '8px',
										marginBottom: '16px',
										textAlign: 'center'
									}}>
										<div style={{ color: '#ff9800', fontSize: '1rem', marginBottom: '8px', fontWeight: 'bold' }}>
											⏳ Подготовка к новой трансляции
										</div>
										<div style={{ color: '#000', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '8px' }}>
											{Math.floor(cooldownSeconds / 60)}:{(cooldownSeconds % 60).toString().padStart(2, '0')}
										</div>
									</div>
								) : (
									<button
										onClick={() => {
											startStream();
											setShowSettingsModal(false);
										}}
										className={styles.startButton}
										disabled={!streamTitle.trim() || !state.transportsReady}
										title={!state.transportsReady ? 'Подключение к серверу...' : ''}
									>
										{t('livestream.startStream')}
									</button>
								)}

								{!state.transportsReady && cooldownSeconds === 0 && (
									<div style={{ color: '#ff9800', fontSize: '0.875rem', marginTop: '8px' }}>
										Подключение к серверу...
									</div>
								)}
							</div>
						) : (
							<div className={styles.settingsForm}>
								<div style={{ color: '#4caf50', fontWeight: 'bold', marginBottom: 16 }}>
									{t('livestream.streamActive')}
								</div>
								<button
									onClick={() => {
										stopStream();
										setShowSettingsModal(false);
									}}
									className={styles.stopButton}
									disabled={isStopping}
								>
									{isStopping ? 'Остановка...' : 'Остановить стрим'}
								</button>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Индикатор загрузки */}
			{isLoading && (
				<div className={styles.loadingOverlay}>
					<div className={styles.loadingText}>
						{t('livestream.connectingToServer')}...
					</div>
				</div>
			)}

			{/* Ошибки */}
			{state.error && (
				<div className={styles.errorOverlay}>
					{t('livestream.error')}: {state.error}
				</div>
			)}

			{/* Модальное окно с инструкцией по сбросу разрешений */}
			{showPermissionsInfo && (
				<div className={styles.modalOverlay} onClick={() => setShowPermissionsInfo(false)}>
					<div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
						<div className={styles.modalHeader}>
							<h3>❌ Доступ к камере и микрофону запрещён</h3>
							<button
								className={styles.closeButton}
								onClick={() => setShowPermissionsInfo(false)}
							>
								✕
							</button>
						</div>
						<div style={{ padding: '20px', color: '#ddd', lineHeight: '1.6' }}>
							<p style={{ marginBottom: '16px', color: '#ff9800' }}>
								<strong>Для запуска стрима необходимо предоставить доступ к камере и микрофону.</strong>
							</p>
							<p style={{ marginBottom: '20px' }}>
								Выберите ваш браузер и следуйте инструкции:
							</p>

							<h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#fff' }}>Safari (iOS/macOS):</h4>
							<ol style={{ paddingLeft: '20px', marginBottom: '20px' }}>
								<li>Нажмите на значок <strong>🔒</strong> или <strong>AA</strong> в адресной строке</li>
								<li>Выберите <strong>"Настройки для этого сайта"</strong></li>
								<li>Установите <strong>Камера</strong> и <strong>Микрофон</strong> в <strong>"Спрашивать"</strong></li>
								<li>Перезагрузите страницу</li>
							</ol>

							<h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#fff' }}>Chrome/Edge:</h4>
							<ol style={{ paddingLeft: '20px', marginBottom: '20px' }}>
								<li>Нажмите на значок <strong>🔒</strong> слева от адреса сайта</li>
								<li>Нажмите <strong>"Настройки сайта"</strong></li>
								<li>Сбросьте разрешения для <strong>Камеры</strong> и <strong>Микрофона</strong></li>
								<li>Перезагрузите страницу</li>
							</ol>

							<h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#fff' }}>Firefox:</h4>
							<ol style={{ paddingLeft: '20px', marginBottom: '20px' }}>
								<li>Нажмите на значок <strong>🔒</strong> в адресной строке</li>
								<li>Нажмите <strong>"Очистить разрешения и снова запросить"</strong></li>
								<li>Или перейдите в <strong>"Настройки" → "Приватность и защита" → "Разрешения"</strong></li>
							</ol>

							<p style={{ marginTop: '20px', padding: '12px', backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: '4px', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
								<strong>💡 После изменения настроек:</strong> Перезагрузите страницу и попробуйте запустить стрим снова.
								Браузер запросит разрешение на доступ к камере и микрофону.
							</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}; 